/**
 * Promotion - 프로모션/쿠폰 정의
 * 
 * Firestore Path: admin/promotions/items/{promotionId}
 */

import { DiscountType } from './SubscriptionData';

export type PromotionStatus = 'active' | 'inactive' | 'expired';

export interface Promotion {
    id: string;

    // 기본 정보
    name: string;                    // "初回契約20%OFF", "年末キャンペーン"
    code?: string;                   // "WELCOME2026" (선택적 - 코드 입력 방식용)
    description?: string;            // 설명

    // 할인 설정
    discountType: DiscountType;      // 'percent' | 'fixed'
    discountValue: number;           // 20 (%) or 2000 (¥)

    // 적용 조건
    targetTiers?: string[];          // 적용 가능한 Tier들 (비어있으면 전체)
    minBillingCycle?: 'monthly' | 'yearly';  // 최소 결제 주기

    // 유효 기간
    validFrom: Date;
    validUntil: Date;

    // 사용 제한
    maxUses?: number;                // 최대 사용 횟수 (0 = 무제한)
    usedCount: number;               // 현재 사용 횟수

    // 상태
    status: PromotionStatus;

    // Audit
    createdBy: string;
    createdAt: Date;
    updatedAt: Date;
}

export const PROMOTIONS_COL = 'admin/promotions/items';

export const defaultPromotion = (): Promotion => ({
    id: '',
    name: '',
    discountType: 'percent',
    discountValue: 0,
    validFrom: new Date(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30일 후
    usedCount: 0,
    status: 'active',
    createdBy: '',
    createdAt: new Date(),
    updatedAt: new Date(),
});

/**
 * 프로모션 유효성 체크
 */
export function isPromotionValid(promotion: Promotion, tier?: string, billingCycle?: 'monthly' | 'yearly'): boolean {
    const now = new Date();

    // 상태 체크
    if (promotion.status !== 'active') return false;

    // 기간 체크
    if (now < promotion.validFrom || now > promotion.validUntil) return false;

    // 사용 횟수 체크
    if (promotion.maxUses && promotion.maxUses > 0 && promotion.usedCount >= promotion.maxUses) return false;

    // Tier 체크
    if (promotion.targetTiers && promotion.targetTiers.length > 0 && tier) {
        if (!promotion.targetTiers.includes(tier)) return false;
    }

    // 결제 주기 체크
    if (promotion.minBillingCycle === 'yearly' && billingCycle === 'monthly') return false;

    return true;
}

/**
 * 프로모션 할인 적용
 */
export function applyPromotion(baseAmount: number, promotion: Promotion): number {
    if (promotion.discountType === 'percent') {
        return Math.round(baseAmount * (1 - promotion.discountValue / 100));
    } else {
        return Math.max(0, baseAmount - promotion.discountValue);
    }
}

/**
 * 프로모션 할인 표시 문자열
 */
export function formatPromotionDiscount(promotion: Promotion): string {
    if (promotion.discountType === 'percent') {
        return `${promotion.discountValue}% OFF`;
    } else {
        return `¥${promotion.discountValue.toLocaleString()} OFF`;
    }
}
